import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Pencil, Loader2, Music4, Link as LinkIcon, FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import AthleteFormDialog from "@/components/AthleteFormDialog";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useSiteLogo } from "@/lib/siteConfig";

const discLabel = { artistica: "Ginnastica Artistica", ritmica: "Ginnastica Ritmica" };

async function urlToImage(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
  const img = await new Promise((resolve, reject) => {
    const im = new window.Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = dataUrl;
  });
  const fmt = dataUrl.substring(dataUrl.indexOf("/") + 1, dataUrl.indexOf(";")).toUpperCase();
  return { dataUrl, width: img.naturalWidth, height: img.naturalHeight, fmt: fmt === "JPG" ? "JPEG" : fmt };
}

const musicOf = (a) => a.music_title ? a.music_title : (a.music_file_id ? "Traccia caricata" : (a.music_url ? "Link esterno" : "—"));
const typesOf = (a) => (a.exercise_types?.length ? a.exercise_types : (a.exercise_type ? [a.exercise_type] : []));

export default function AthletesSection() {
  const { user } = useAuth();
  const canManage = ["admin", "segreteria"].includes(user?.role);
  const logoUrl = useSiteLogo();
  const [athletes, setAthletes] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("ritmica");
  const [eventFilter, setEventFilter] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, e] = await Promise.all([api.get("/athletes"), api.get("/events")]);
      setAthletes(a.data);
      setEvents(e.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/athletes/${id}`);
      toast.success("Iscrizione rimossa");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setOpen(true); };

  const tabEvents = events.filter((e) => e.discipline === tab);
  const changeTab = (v) => { setTab(v); setEventFilter("all"); setLevelFilter("all"); };
  const levelOptions = [...new Set(athletes.filter((a) => a.discipline === tab && a.level).map((a) => a.level))].sort();
  const filtered = athletes.filter((a) => a.discipline === tab && (eventFilter === "all" || a.event_id === eventFilter) && (levelFilter === "all" || a.level === levelFilter));
  const selectedEvent = events.find((e) => e.id === eventFilter);

  const [exportingXlsx, setExportingXlsx] = useState(false);
  const exportXLSX = async () => {
    if (filtered.length === 0) { toast.error("Nessun atleta da esportare"); return; }
    setExportingXlsx(true);
    try {
      const params = new URLSearchParams({ discipline: tab });
      if (eventFilter !== "all") params.set("event_id", eventFilter);
      if (levelFilter !== "all") params.set("level", levelFilter);
      const res = await api.get(`/athletes/export.xlsx?${params.toString()}`, { responseType: "blob" });
      const m = /filename="?([^";]+)"?/.exec(res.headers["content-disposition"] || "");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = m ? m[1] : `iscrizioni-${tab}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      toast.success(`Excel scaricato (${filtered.length} iscritti)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Esportazione non riuscita"); }
    finally { setExportingXlsx(false); }
  };

  const exportPDF = async () => {
    if (filtered.length === 0) { toast.error("Nessun atleta da esportare"); return; }
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const now = new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
    let textX = 14;
    const headerBottom = 34;
    if (logoUrl) {
      try {
        const logo = await urlToImage(logoUrl);
        const h = 18, w = Math.min(40, (logo.width / logo.height) * h);
        doc.addImage(logo.dataUrl, logo.fmt, 14, 10, w, h);
        textX = 14 + w + 5;
      } catch (e) { /* logo opzionale */ }
    }
    doc.setFontSize(18); doc.setTextColor(20, 20, 20);
    doc.text("OPES Ginnastiche", textX, 18);
    doc.setFontSize(13); doc.setTextColor(90, 90, 90);
    const subtitle = selectedEvent ? `Iscrizioni — ${discLabel[tab]} · ${selectedEvent.title}` : `Iscrizioni atleti — ${discLabel[tab]}`;
    doc.text(subtitle, textX, 26);
    doc.setFontSize(9); doc.text(`${filtered.length} iscritti · Generato il ${now}`, textX, 32);
    doc.setDrawColor(212, 175, 55); doc.setLineWidth(0.5); doc.line(14, headerBottom, 283, headerBottom);

    const head = [["#", "Atleta", "Società", "Categoria", "Livello", "Tipo", "Attrezzi", "Musica"]];
    const body = filtered.map((a, i) => {
      const name = `${a.first_name} ${a.last_name}`;
      const app = (a.apparatus || []).join(", ") || "—";
      return [i + 1, name, a.club || "—", a.category || "—", a.level || "—", typesOf(a).join(", ") || "—", app, musicOf(a)];
    });
    autoTable(doc, {
      startY: headerBottom + 8,
      head, body,
      theme: "grid",
      headStyles: { fillColor: [212, 175, 55], textColor: [2, 6, 23], fontStyle: "bold" },
      styles: { fontSize: 8.5, cellPadding: 2 },
      columnStyles: { 0: { halign: "center", cellWidth: 10 } },
      margin: { left: 14, right: 14 },
    });
    const slug = selectedEvent ? "-" + selectedEvent.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30) : "";
    doc.save(`iscrizioni-${tab}${slug}-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF generato");
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <p className="text-muted-foreground">Iscrizioni degli atleti divise per disciplina.</p>
        <div className="flex items-center gap-2">
          {canManage && (
            <>
              <Button data-testid="export-athletes-xlsx" onClick={exportXLSX} disabled={exportingXlsx} variant="outline" className="border-white/15 hover:border-gold/60">
                {exportingXlsx ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1.5" />} Esporta Excel
              </Button>
              <Button data-testid="export-athletes-pdf" onClick={exportPDF} variant="outline" className="border-white/15 hover:border-gold/60">
                <FileDown className="w-4 h-4 mr-1.5" /> Esporta PDF
              </Button>
            </>
          )}
          <Button data-testid="add-athlete-btn" onClick={openNew} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Iscrivi Atleta — {tab === "ritmica" ? "Ritmica" : "Artistica"}
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="bg-[#0F172A] border border-white/10 h-auto p-1 mb-6">
          <TabsTrigger data-testid="tab-ritmica" value="ritmica" className="data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-5 py-2">Ginnastica Ritmica</TabsTrigger>
          <TabsTrigger data-testid="tab-artistica" value="artistica" className="data-[state=active]:bg-gold data-[state=active]:text-[#020617] px-5 py-2">Ginnastica Artistica</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex items-center gap-3 mb-6">
        <span className="text-sm text-muted-foreground">Filtra per evento:</span>
        <Select value={eventFilter} onValueChange={setEventFilter}>
          <SelectTrigger data-testid="event-filter-select" className="w-72 max-w-full"><SelectValue placeholder="Tutti gli eventi" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli eventi</SelectItem>
            {tabEvents.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}
          </SelectContent>
        </Select>
        {eventFilter !== "all" && <span className="text-xs text-gold" data-testid="event-filter-count">{filtered.length} iscritti</span>}
        {levelOptions.length > 0 && (
          <>
            <span className="text-sm text-muted-foreground ml-2">Livello:</span>
            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger data-testid="level-filter-select" className="w-44 max-w-full"><SelectValue placeholder="Tutti i livelli" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i livelli</SelectItem>
                {levelOptions.map((lv) => <SelectItem key={lv} value={lv}>{lv}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground border border-dashed border-white/10 rounded-xl">
          {eventFilter === "all" ? "Nessun atleta iscritto in questa disciplina." : "Nessun atleta iscritto a questo evento."}
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl border border-white/10 bg-[#0F172A] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead>Atleta</TableHead>
                <TableHead>Società</TableHead>
                <TableHead>Categoria</TableHead>
                {tab === "ritmica" && <TableHead>Livello</TableHead>}
                <TableHead>Tipo esibizione</TableHead>
                <TableHead>Musica</TableHead>
                {canManage && <TableHead className="text-right">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((a) => (
                <TableRow key={a.id} data-testid={`athlete-row-${a.id}`} className="border-white/10">
                  <TableCell className="font-medium">{a.first_name} {a.last_name}</TableCell>
                  <TableCell className="text-muted-foreground">{a.club || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {a.category || "—"}
                    {tab === "artistica" && a.apparatus?.length ? <span className="block text-xs text-gold/70">{a.apparatus.join(", ")}</span> : null}
                  </TableCell>
                  {tab === "ritmica" && <TableCell className="text-muted-foreground">{a.level || "—"}</TableCell>}
                  <TableCell className="text-muted-foreground">
                    {typesOf(a).length ? (
                      <div className="flex flex-wrap gap-1" data-testid={`athlete-types-${a.id}`}>
                        {typesOf(a).map((t) => <Badge key={t} variant="outline" className="border-gold/30 text-gold text-[10px] capitalize">{t.toLowerCase()}</Badge>)}
                      </div>
                    ) : "—"}
                    {tab === "ritmica" && a.apparatus?.length ? <span className="block text-xs text-gold/70 mt-1">{a.apparatus.join(", ")}</span> : null}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {a.music_title || a.music_file_id ? <span className="flex items-center gap-1.5"><Music4 className="w-3.5 h-3.5 text-gold" />{a.music_title || "Traccia"}</span>
                      : a.music_url ? <span className="flex items-center gap-1.5"><LinkIcon className="w-3.5 h-3.5 text-gold" />Link esterno</span>
                      : "—"}
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button data-testid={`edit-athlete-${a.id}`} onClick={() => openEdit(a)} className="text-muted-foreground hover:text-gold transition-colors"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`delete-athlete-${a.id}`} onClick={() => remove(a.id)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </motion.div>
      )}

      <AthleteFormDialog
        open={open}
        onOpenChange={setOpen}
        discipline={editing ? editing.discipline : tab}
        events={events}
        editing={editing}
        onSaved={load}
      />
    </div>
  );
}
